import { StorageClient } from '@atlaskit/frontend-utilities/StorageClient';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { Choreographer, ProductIds } from '@atlassian/ipm-choreographer';

const DEBUG_BYPASS_SESSION_STORAGE_KEY = 'postOffice.choreographer.debug';
const DEBUG_BYPASS_SESSION_STORAGE_ITEM_KEY = 'bypassEnabled';

const getStorageClient = (): StorageClient =>
	new StorageClient(DEBUG_BYPASS_SESSION_STORAGE_KEY, {
		storageEngine: 'sessionStorage',
	});

type ChoreographerDebugMessage = {
	productId: ProductIds;
	messageId: string;
	timestamp?: number;
};

type ChoreographerDebugEvent = {
	blockedByMessageId?: string;
	blockedByProductId?: ProductIds;
	disposition?: string;
	messageId?: string;
	phase?: string;
	productId?: ProductIds;
	timestamp: number;
};

type ChoreographerDebugSnapshot = {
	activeMessage: ChoreographerDebugMessage | null;
	bypassEnabled: boolean;
	pendingMessage: ChoreographerDebugMessage | null;
	queuedMessages: ChoreographerDebugMessage[];
	recentEvents: ChoreographerDebugEvent[];
	registeredMessages: Array<{
		productId: ProductIds;
		messageIds: string[];
	}>;
	registeredPlugins: ProductIds[];
	updatedAt: number;
};

type ChoreographerDevtoolsBridge = {
	getSnapshot: () => ChoreographerDebugSnapshot;
	isBypassEnabled: () => boolean;
	setBypassEnabled: (isEnabled: boolean) => void;
};

type WindowWithChoreographerDevtools = Window & {
	choreographerRegistry?: Map<ProductIds, unknown>;
	postOffice?: {
		choreographer?: {
			debug?: ChoreographerDevtoolsBridge;
			singleton?: Choreographer;
		};
	};
};

const getWindow = (): WindowWithChoreographerDevtools | undefined =>
	typeof window === 'undefined' ? undefined : (window as WindowWithChoreographerDevtools);

const getPersistedBypassEnabled = () => {
	return getStorageClient().getItem(DEBUG_BYPASS_SESSION_STORAGE_ITEM_KEY) === true;
};

export const shouldBypassChoreographerFromDevtools = (): boolean => {
	return fg('post-office_choreographer_devtools_bridge') && getPersistedBypassEnabled();
};

const setPersistedBypassEnabled = (isEnabled: boolean) => {
	// The devtools bridge should never break message rendering when browser storage is blocked.
	// StorageClient handles exceptions internally.
	getStorageClient().setItemWithExpiry(DEBUG_BYPASS_SESSION_STORAGE_ITEM_KEY, isEnabled);
};

const getChoreographerSingleton = (): Choreographer | undefined =>
	getWindow()?.postOffice?.choreographer?.singleton;

const getRegisteredPluginIds = () => {
	return [...(getWindow()?.choreographerRegistry?.keys() ?? [])];
};

const getSnapshot = (): ChoreographerDebugSnapshot => {
	const singleton = getChoreographerSingleton();
	const registeredPlugins = getRegisteredPluginIds();

	return {
		activeMessage: singleton?.getCurrentMessage() ?? null,
		bypassEnabled: shouldBypassChoreographerFromDevtools(),
		pendingMessage: singleton?.getCurrentPendingMessage() ?? null,
		queuedMessages: singleton?.getQueuedMessages() ?? [],
		recentEvents: [],
		registeredMessages: registeredPlugins.map((productId) => ({
			productId,
			messageIds: [...(singleton?.getMessages(productId).keys() ?? [])],
		})),
		registeredPlugins,
		updatedAt: Date.now(),
	};
};

export const installChoreographerDevtoolsBridge = ({
	setChoreographerEnabled,
}: {
	setChoreographerEnabled: (isEnabled: boolean) => void;
}): (() => void) => {
	const currentWindow = getWindow();
	if (!currentWindow) {
		return () => {};
	}

	if (!fg('post-office_choreographer_devtools_bridge')) {
		delete currentWindow.postOffice?.choreographer?.debug;
		return () => {};
	}

	const bridge: ChoreographerDevtoolsBridge = {
		getSnapshot,
		isBypassEnabled: shouldBypassChoreographerFromDevtools,
		setBypassEnabled: (isEnabled: boolean) => {
			setPersistedBypassEnabled(isEnabled);
			setChoreographerEnabled(!isEnabled);
		},
	};

	currentWindow.postOffice = {
		...currentWindow.postOffice,
		choreographer: {
			...currentWindow.postOffice?.choreographer,
			debug: bridge,
		},
	};

	return () => {
		if (currentWindow.postOffice?.choreographer?.debug === bridge) {
			delete currentWindow.postOffice.choreographer.debug;
		}
	};
};
